import React from 'react';
import Modal from '../../../../../shared/Modal/Modal';
import PostTrainingSurvey from '../PostTrainingSurvey';
import { useAdminProgressQuery } from '../../../../useAdminProgressQuery';
import {
  getCertificateNameFontSize,
  loadCertificateTemplate,
} from './certificateTemplate';

const CertificateTemplateUrl =
  '/resource-library/EzTraining_Certificate.svg';

const CertificateTemplateOptions = {
  // The supplied SVG converts its sample name and date to outlined paths.
  excludedPathIndexes: [238, 239],
  textTargets: {
    name: {
      x: 399,
      y: 253,
      dominantBaseline: 'middle',
      textAnchor: 'middle',
      fontSize: 36,
      fill: '#000000',
    },
    date: {
      x: 397,
      y: 464,
      dominantBaseline: 'middle',
      textAnchor: 'middle',
      fontSize: 20,
      fill: '#000000',
    },
  },
};

const CertificateDownloadLink = React.lazy(
  () => import('./CertificateDownloadLink'),
);

const getCertificateFileName = (name) => {
  const safeName = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return `${safeName || 'facilitator'}-eztraining-certificate.pdf`;
};

const formatCertificateDate = (completedAt) => {
  const dateParts = completedAt?.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!dateParts) {
    return '';
  }

  const [, year, month, day] = dateParts;
  const completionDate = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  );

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(completionDate);
};

const CertificatePreview = ({
  template,
  facilitatorName,
  date,
  statusMessage,
}) => {
  const nameTarget = template?.textTargets.name;
  const dateTarget = template?.textTargets.date;
  const nameFontSize = nameTarget
    ? getCertificateNameFontSize(facilitatorName, nameTarget.fontSize)
    : 36;

  return (
    <>
      <div className='border-b border-slate-300 bg-white px-4 py-3 text-center text-lg text-slate-900'>
        {statusMessage}
      </div>
      <div className='pointer-events-none bg-white p-4'>
        {template ? (
          <svg
            viewBox={template.viewBox}
            width={template.width}
            height={template.height}
            role='img'
            aria-label={`ezTraining certificate for ${facilitatorName}`}
            className='h-auto w-full rounded border border-slate-300 bg-white'
          >
            {template.paths.map((path) => (
              <path
                key={path.key}
                d={path.d}
                fill={path.fill}
                stroke={path.stroke}
                strokeWidth={path.strokeWidth}
                strokeMiterlimit={path.strokeMiterlimit}
              />
            ))}
            <text
              id='name'
              x={nameTarget.x}
              y={nameTarget.y}
              dominantBaseline={nameTarget.dominantBaseline}
              textAnchor={nameTarget.textAnchor}
              fontFamily='Helvetica, Arial, sans-serif'
              fontSize={nameFontSize}
              fill={nameTarget.fill}
            >
              {facilitatorName}
            </text>
            <text
              id='date'
              x={dateTarget.x}
              y={dateTarget.y}
              dominantBaseline={dateTarget.dominantBaseline}
              textAnchor={dateTarget.textAnchor}
              fontFamily='Helvetica, Arial, sans-serif'
              fontSize={dateTarget.fontSize}
              fill={dateTarget.fill}
            >
              {date}
            </text>
          </svg>
        ) : (
          <div
            aria-hidden='true'
            className='w-full rounded border border-slate-200 bg-slate-50'
            style={{ aspectRatio: '792 / 612' }}
          />
        )}
      </div>
    </>
  );
};

const HybridCertificate = () => {
  const { adminProgress, hasLoadedAdminProgress } = useAdminProgressQuery({
    currentPath: '',
  });
  const hasCompletedFacilitatorPostTrainingSurvey =
    adminProgress.postSurveyCompleted;
  const [showSurvey, setShowSurvey] = React.useState(false);
  const [certificateTemplate, setCertificateTemplate] = React.useState(null);
  const [certificateError, setCertificateError] = React.useState('');
  const facilitatorName = localStorage.getItem('name')?.trim() || 'Facilitator';
  const completionDate = React.useMemo(
    () => formatCertificateDate(adminProgress.postSurveyCompletedAt),
    [adminProgress.postSurveyCompletedAt],
  );

  React.useEffect(() => {
    if (!hasLoadedAdminProgress) {
      return;
    }

    setShowSurvey(!hasCompletedFacilitatorPostTrainingSurvey);
  }, [hasCompletedFacilitatorPostTrainingSurvey, hasLoadedAdminProgress]);

  React.useEffect(() => {
    let isCurrent = true;

    loadCertificateTemplate(
      CertificateTemplateUrl,
      CertificateTemplateOptions,
    )
      .then((template) => {
        if (isCurrent) {
          setCertificateTemplate(template);
        }
      })
      .catch((error) => {
        if (isCurrent) {
          setCertificateError(error.message);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  const renderCertificate = (statusMessage) => (
    <CertificatePreview
      template={certificateTemplate}
      facilitatorName={facilitatorName}
      date={completionDate}
      statusMessage={statusMessage}
    />
  );

  return (
    <>
      <Modal
        noPortal
        show={showSurvey}
        showModal={setShowSurvey}
        disableDismiss
        className='modal-wrap--above-footer'
      >
        <PostTrainingSurvey
          closeModal={async (responses) => {
            setShowSurvey(false);
          }}
        />
      </Modal>
      <div className='flex flex-col items-center p-4 text-left'>
        <div className='w-full max-w-2xl'>
          <h1 className='mb-4 text-3xl font-bold'>Certificate</h1>
        </div>
        {certificateTemplate ? (
          <React.Suspense
            fallback={
              <div className='block w-full max-w-2xl overflow-hidden rounded-lg border border-slate-300 bg-white shadow'>
                {renderCertificate('Preparing your printable certificate...')}
              </div>
            }
          >
            <CertificateDownloadLink
              template={certificateTemplate}
              facilitatorName={facilitatorName}
              date={completionDate}
              fileName={getCertificateFileName(facilitatorName)}
              className='block w-full max-w-2xl overflow-hidden rounded-lg border border-slate-300 bg-white no-underline shadow transition hover:border-purple hover:shadow-md'
            >
              {({ loading, error }) =>
                renderCertificate(
                  error
                    ? 'Unable to prepare the certificate. Please try again.'
                    : loading
                      ? 'Preparing your printable certificate...'
                      : 'Download your printable ezTraining certificate',
                )
              }
            </CertificateDownloadLink>
          </React.Suspense>
        ) : (
          <div className='block w-full max-w-2xl overflow-hidden rounded-lg border border-slate-300 bg-white shadow'>
            {renderCertificate(
              certificateError
                ? 'Unable to load the certificate. Please refresh and try again.'
                : 'Preparing your printable certificate...',
            )}
          </div>
        )}
        {certificateError ? (
          <p
            role='alert'
            className='mt-3 w-full max-w-2xl text-sm text-red-700'
          >
            {certificateError}
          </p>
        ) : null}
      </div>
    </>
  );
};

export default HybridCertificate;
